import Head from 'next/head'
import { useRouter } from 'next/router'
import Prefooter from '@/components/particles/prefooter/index'
import { selectPropertiesCount, selectPropertiesStatus, selectPropertiesResult, selectAllResults, fetchByCriteria, setResults, setTotal } from '@/app/reducers/properties'
import { selectFilter, serializeFilterValue } from '@/app/reducers/filters'
import { STATUS, ePropertyCondition, eOriginId } from '@/app/globalContext/globalContext.interfaces'
import { IFilterContext } from '@/app/globalContext/filterContext.interface'
import { IFiltros, IEstructuraPrincipal } from '@/components/templates/search/interfaces/IEstructuraPrincipal'
import { setEstructuraValues, getCommunesAndNeighborhoodsList, formatRegionValues, formatComunaValues, formatBarrioValues } from '@/utils/listaSeo'
import { ContainerStyled } from '@/components/templates/customMainContainer'
import { ListadoPropiedades } from '@/components/molecules/cardPropiedad'
import { PaginationIndex } from '@/components/particles'
import { FooterIndex } from '@/components/particles/footer/index'
import { TextoSeo } from '@/components/atoms/textoSeo/TextoSeo'
import { NotFound } from '@/components/particles/notFound/index'
import { Carousel, Loader as GlobalLoader } from '@toctoc/design-system'
import { IBanner } from '@/components/templates/search/interfaces/IEstructuraPrincipal'
import { getSchema } from '../../../../layout/_schema'
import { getBreadcrumbSchema } from '../../../../layout/_breadcrumbSchema'
import { Breadcrumbs } from '@/components/molecules/breadcrumbs'
import { GoogleTagManager } from '@/utils/browser/googleTagManager/GoogleTagManager'
import { VWO } from '@/utils/VWO/VWO'
import { useBannerUpdater } from '../../../hooks/useBannerUpdater'
import { ListaSEOType } from '@/utils/types'
import { generateBajadaTitulo, getBreadcrumbsSegments } from '@/utils/listaSeo'
import { usePropertySearch } from 'src/hooks/usePropertySearch'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import React, { useRef, useState, useEffect } from 'react'
import dynamic from 'next/dynamic'
import { selectSessionState, setTempLogInData } from '@/app/reducers/session'
import { selectLeadState, sendLead, setSelectedPropertyData, setShowContactStatusModal, setShowGuestSignUpModal, setShowQuoteStatusModal } from '@/app/reducers/lead'
import { selectFiltersAvailable, setFiltersAvailable } from '@/app/reducers/filtersAvailable'
import { selectFilterDataState, filterDataGet } from '@/app/reducers/filtersData'
import { selectFavoritesState, sendFavorite, setFavorite, toggleFavoriteOptimistic } from '@/app/reducers/favorites'
import { selectSavedSearchUrl, sendUserSearch, setSavedUrl } from '@/app/reducers/userSearch'
import { GuestSignUpModal } from '@/components/organisms/guestSignUpModal/GuestSignUpModal'
import { QuoteStatusModal } from '@/components/organisms/quoteStatusModal/QuoteStatusModal'
import { ContactStatusModal } from '@/components/organisms/contactStatusModal/ContactStatusModal'
import { IPropertyCard } from '@/components/templates/search/interfaces'
import { GeoJSONPolygon } from '@/components/templates/search/interfaces/IPropertyList'
import { LeadRequest } from '@/components/templates/search/interfaces/ILeadRequest'
import { TempUserData } from '@/components/templates/search/interfaces/ISessionData'
import { SearchFilterBar, ViewMode } from './SearchFilterBar'
import type { FiltersControl } from '@/components/templates/filters/Filters.interface'
import { FilterChips } from './FilterChips'
import { FiltersDrawer } from './FiltersDrawer'
import { OrdenarPorV2 } from './OrdenarPorV2'
import { MobileMapHeader } from './MobileMapHeader'
import { FloatingMapButton } from './FloatingMapButton'
import { MobileBottomSheet, BottomSheetSnap } from './MobileBottomSheet'
import { MiniPropertyCard } from './MiniPropertyCard'
import { MobilePropertyDetailModal } from './MobilePropertyDetailModal'
import { PropertyCardSkeletonGrid } from './PropertyCardSkeleton'
import styles from './ListaSEOV2Layout.module.scss'

const MapboxMap = dynamic(() => import('./MapboxMap'), { ssr: false })

// Parámetro que viaja en la URL de retorno del SSO para saber, al volver logeado, qué
// propiedad marcar como favorita automáticamente — se limpia de la URL una vez aplicado.
const FAVORITE_RETURN_PARAM = 'favProp'

// Parámetro que viaja en la URL de retorno del SSO para saber, al volver logeado, que se debe
// guardar automáticamente la búsqueda actual — se limpia de la URL una vez aplicado.
const SAVE_SEARCH_RETURN_PARAM = 'saveSearch'

export const ListaSEOV2Layout = (props: ListaSEOType) => {
	const dispatch = useAppDispatch();
	const filterState = useAppSelector(selectFilter);
	const filterStateSerialized = serializeFilterValue(filterState)
	const propertiesCountState = useAppSelector(selectPropertiesCount);
	const propertiesStatusState = useAppSelector(selectPropertiesStatus);
	const propertiesList = useAppSelector(selectPropertiesResult);
	const mapProperties = useAppSelector(selectAllResults);
	const filtersAvailableState = useAppSelector(selectFiltersAvailable);
	const filterDataState = useAppSelector(selectFilterDataState);
	const [polygon, setPolygon] = useState<GeoJSONPolygon | null>(null);
	const leadState = useAppSelector(selectLeadState);
	const { status: leadStatus, leadStatusInfo, showGuestSignUpModal, selectedPropertyData, showQuoteStatusModal, showContactStatusModal } = leadState ?? {};
	const sessionInfo = useAppSelector(selectSessionState);
	const { userData, isLogin } = sessionInfo ?? {};
	const favoritesState = useAppSelector(selectFavoritesState);
	const router = useRouter();
	const { pathListaSeo } = router.query;
	const { urlSegments, quoteAndContactEnabled, urlTocTocImg } = props;

	// Estructura dinámica: se actualiza client-side cuando el path cambia por shallow nav.
	// Siempre refleja lo que dice Mongo para el path actual, sin depender de getStaticProps.
	const [estructuraDinamica, setEstructuraDinamica] = useState<IEstructuraPrincipal>(props.estructuraPrincipal)
	const [filtrosBaseDinamica, setFiltrosBaseDinamica] = useState(props.filtrosBase)
	const estructuraPathRef = useRef<string>((pathListaSeo as string[] ?? []).join('/'))

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (!router.isReady) return
		const currentPath = (router.query.pathListaSeo as string[] ?? []).join('/')
		if (currentPath === estructuraPathRef.current) return
		estructuraPathRef.current = currentPath

		const baseApi = process.env.NEXT_PUBLIC_HOME_BASE_API
		fetch(`${baseApi}/listas/directoriosinslug/${currentPath}`)
			.then(r => r.json())
			.then(async (nueva: IEstructuraPrincipal) => {
				if (!nueva?.filtros?.length) return
				// Si Mongo no tiene layoutVersion, getStaticProps lo defaultea a 'v1' — hacemos lo mismo
				const version = nueva.layoutVersion ?? 'v1'
				if (version !== 'v2') {
					// El nuevo path es v1 según Mongo — recargamos para que getStaticProps decida
					window.location.href = router.asPath
					return
				}

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const nuevoFiltroBase = (nueva as any).filtroBase ?? filtrosBaseDinamica

				// Poblamiento de filtros: usar exactamente la misma lógica que SSR (getListaSeoStaticProps)
				// para asegurar que shallow-nav y SSR muestren datos consistentes
				try {
					const { setupServices } = await import('services.setup.client')
					const services = setupServices()

					// 1. Cargar regiones si el filtro de región llega vacío desde MongoDB
					const regionFilter = nueva.filtros.find(f => f.id === 'region')
					if (regionFilter && (!regionFilter.values || regionFilter.values.length === 0)) {
						const regiones = await services.getRegiones(undefined, currentPath)
						if (regiones?.length > 0) {
							nueva = setEstructuraValues(nueva, 'region', formatRegionValues, regiones)
						}
					}

					// 2. Cargar comunas y barrios según lo que preseleccione filtroBase
					// (mismo flujo que getCommunesAndNeighborhoodsList en SSR)
					const regiones = await services.getRegiones(undefined, currentPath)
					if (regiones?.length > 0) {
						const { listaComunas, listaBarrios } = await getCommunesAndNeighborhoodsList(
							nuevoFiltroBase,
							regiones,
							{ originkey: 'somoslosmejores', cache: 'force-cache' },
							services.getComunas,
							services.getBarrios,
							nueva.url
						)

						if (listaComunas.length > 0) {
							nueva = setEstructuraValues(nueva, 'comuna', formatComunaValues, listaComunas)
						}

						if (listaBarrios.length > 0) {
							nueva = setEstructuraValues(nueva, 'barrio', formatBarrioValues, listaBarrios)
						}
					}
				} catch (e) {
					// Si falla el poblamiento, los filtros quedan con values vacíos (mismo que si no llega la data)
				}

				// Una vez que nueva.filtros está poblado, actualizar el estado
				// Esto previene que Filters.tsx:116-121 sincronice un estado vacío que pise datos cargados
				setEstructuraDinamica(nueva)
				setFiltrosBaseDinamica(nuevoFiltroBase)
			})
			.catch(() => { /* falla silenciosa: mantiene estructura anterior */ })
	}, [router.query.pathListaSeo, router.isReady])

	// Cargar comunas/barrios cuando hay región en filterState (initial mount o cambio por usuario)
	const lastLoadedRegionFromFilterStateRef = useRef<number | null>(null)
	// Extraer solo el valor de región para usar como dependencia (evita re-runs innecesarios)
	const regionValueFromFilterState = filterState.filtros.find(f => f.id === 'region')?.values?.[0]?.value?.[0] ?? null
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (!regionValueFromFilterState) return

		// Solo cargar si la región cambió (inicial mount o usuario selecciona otra región)
		if (lastLoadedRegionFromFilterStateRef.current === regionValueFromFilterState) return
		lastLoadedRegionFromFilterStateRef.current = regionValueFromFilterState

		// Cargar comunas para esta región
		dispatch(filterDataGet({ filterId: 'comuna', value: regionValueFromFilterState.toString(), pathList: '' }))
	}, [regionValueFromFilterState, dispatch])

	// Limpiar flag 'b' cuando cambian filtros geográficos (región/comuna/barrio/avenida/calle/pasaje)
	// Esto permite que MapboxMap centre el mapa en el nuevo polígono
	const lastGeoFiltersRef = useRef<string>('')
	const [geoFilterChangeKey, setGeoFilterChangeKey] = useState<string>('')
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const geoFilterIds = ['region', 'comuna', 'barrio', 'avenida', 'calle', 'pasaje']
		const currentGeoFilters = geoFilterIds
			.map(id => filterState.filtros.find(f => f.id === id)?.values?.[0]?.label ?? '')
			.join('|')

		// Solo proceder si algún filtro geográfico cambió
		if (currentGeoFilters === lastGeoFiltersRef.current || !currentGeoFilters) return
		lastGeoFiltersRef.current = currentGeoFilters

		// Actualizar key para que MapboxMap resete userHasMovedRef
		setGeoFilterChangeKey(currentGeoFilters)

		// Limpiar el flag 'b' para que el mapa centre en el nuevo polígono
		// MapboxMap hace fitBounds automáticamente cuando recibe el nuevo polígono
		if (router.query.b) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const newQuery: any = { ...router.query }
			delete newQuery.b
			router.replace({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true })
		}
	}, [filterState.filtros, router])

	// Integrar datos dinámicos de filtros (comunas, barrios) cuando filterDataState cambia
	const lastUpdatedFilterRef = useRef<string | null>(null)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (!filterDataState.filtersData || filterDataState.filtersData.length === 0) return

		const updatedFilterData = filterDataState.filtersData[0]
		if (!updatedFilterData || !updatedFilterData.id) return

		// Evitar actualizar si es el mismo filtro que ya procesamos
		if (lastUpdatedFilterRef.current === updatedFilterData.id) return
		lastUpdatedFilterRef.current = updatedFilterData.id

		// Actualizar filtro si existe, o agregarlo si no existe
		const filterExists = filtersAvailableState.filtros.some(f => f.id === updatedFilterData.id)
		let updatedFilters

		if (filterExists) {
			updatedFilters = filtersAvailableState.filtros.map(f =>
				f.id === updatedFilterData.id
					? { ...f, values: updatedFilterData.values }
					: f
			)
		} else {
			// Crear un nuevo filtro con los datos dinámicos
			const newFilter: IFiltros = {
				id: updatedFilterData.id,
				type: updatedFilterData.type || 'checkbox',
				name: updatedFilterData.id,
				values: updatedFilterData.values,
				dependsOn: []
			}
			updatedFilters = [...filtersAvailableState.filtros, newFilter]
		}

		dispatch(setFiltersAvailable({ filtros: updatedFilters }))
	}, [filterDataState.filtersData])

	const { tof, tpf, region, comuna, chainSegment } = getBreadcrumbsSegments(urlSegments, pathListaSeo as string[]);

	// SSR siempre arranca en lista (viewMode=0) para que Google Bot vea el HTML de propiedades.
	// El modo real desde la URL se aplica solo en el cliente, después de la hidratación.
	const [viewMode, setViewMode] = useState<ViewMode>(0);
	// PRUEBA: snap "half" (lista+mapa) deshabilitado en mobile — arranca en 'collapsed'.
	// Para revertir, volver a: useState<BottomSheetSnap>('half')
	const [bottomSheetSnap, setBottomSheetSnap] = useState<BottomSheetSnap>('collapsed');
	const [hoveredPropertyId, setHoveredPropertyId] = useState<number | null>(null);
	const [selectedMarkerProperty, setSelectedMarkerProperty] = useState<IPropertyCard | null>(null);
	const visiblePropertyIdsRef = useRef<number[]>([]);
	const [detailProperty, setDetailProperty] = useState<IPropertyCard | null>(null);
	const removeFilterRef = useRef<FiltersControl | null>(null);
	const pendingMapRequestRef = useRef<{ abort: () => void } | null>(null);
	// Ref con el filtro viewboard actual — se inyecta en cada dispatch de usePropertySearch en vista mapa
	const viewboardFiltroRef = useRef<IFiltros[]>([]);
	// Último viewboard+zoom calculado por el mapa — para incluirlo en pushes de viewMode
	// (router.query puede estar desactualizado cuando handleViewModeChange se ejecuta justo después de handleViewChange)
	const latestMapQueryRef = useRef<Record<string, string>>({});
	// Trackear el viewMode anterior para saber cuándo se ENTRA al modo mapa desde la lista
	const prevViewModeRef = useRef<ViewMode>(0);
	// Última búsqueda de LISTA (sin viewboard) — fetchByCriteria comparte results/total/allResults
	// entre lista y mapa, así que la búsqueda acotada al viewport del mapa pisa estos campos.
	// Se cachea acá para restaurarla al volver a lista, sin tener que volver a buscar.
	const lastListResultsRef = useRef<{ results: IPropertyCard[]; total: number } | null>(null);
	useEffect(() => {
		if (viewMode === 0) {
			lastListResultsRef.current = { results: propertiesList, total: propertiesCountState };
		}
	}, [viewMode, propertiesList, propertiesCountState]);

	useEffect(() => {
		if (!router.isReady) return;
		const vb = router.query.view;
		// PRUEBA: vista "lista+mapa" (viewMode 2) deshabilitada en mobile.
		// Visitas que llegan con ?view=ml en mobile se tratan como mapa completo (viewMode 1).
		// Para revertir, volver a: const mode: ViewMode = vb === 'm' ? 1 : vb === 'ml' ? 2 : 0;
		const isMobileNow = typeof window !== 'undefined' && window.innerWidth <= 767;
		const mode: ViewMode = vb === 'm' ? 1 : vb === 'ml' ? (isMobileNow ? 1 : 2) : 0;
		setViewMode(mode);
	}, [router.query.view, router.isReady]);

	// En mobile con mapa activo: bloquear scroll del body.
	// El snap solo se resetea al ENTRAR desde lista (viewMode 0→1/2) para no interferir
	// con los cambios de snap que el usuario hace arrastrando el sheet.
	useEffect(() => {
		const prevMode = prevViewModeRef.current;
		const isMobileMapActive = window.innerWidth <= 767 && viewMode !== 0;
		const enteringFromList = prevMode === 0 && viewMode !== 0;
		const enteringListFromMap = prevMode !== 0 && viewMode === 0;
		prevViewModeRef.current = viewMode;
		if (isMobileMapActive && enteringFromList) {
			// PRUEBA: viewMode nunca es 2 en mobile — esto siempre resuelve a 'collapsed'.
			// Para revertir junto con el resto de "half": viewMode === 2 ? 'half' : 'collapsed'
			setBottomSheetSnap('collapsed');
		}
		if (enteringListFromMap) {
			// Cancelar un fetch de mapa en vuelo para que no pise la restauración de abajo al resolver tarde.
			pendingMapRequestRef.current?.abort();
			// Restaurar los resultados de lista cacheados — sin volver a buscar.
			if (lastListResultsRef.current) {
				dispatch(setResults(lastListResultsRef.current.results));
				dispatch(setTotal(lastListResultsRef.current.total));
			}
		}
		document.body.style.overflow = isMobileMapActive ? 'hidden' : '';
		return () => { document.body.style.overflow = ''; };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewMode]);

	// Parsear viewboard y zoom desde la URL (solo se usan como posición inicial del mapa)
	const initialBounds = (() => {
		const raw = router.query.viewboard as string | undefined;
		if (!raw) return null;
		const parts = raw.split(',').map(Number);
		if (parts.length !== 4 || parts.some(isNaN)) return null;
		return parts as [number, number, number, number];
	})();
	const initialZoom = router.query.zoom ? Number(router.query.zoom) : null;

	// En modo mapa sin viewboard en la URL, el primer fetch de propiedades lo dispara el mapa
	// mediante handleViewChange (que tiene el viewboard exacto). Bloqueamos el auto-execute de
	// usePropertySearch hasta que el mapa haya llamado handleViewChange al menos una vez.
	const urlView = router.query.view as string | undefined
	const isUrlMapMode = urlView === 'm' || urlView === 'ml'
	const mapViewboardReadyRef = useRef(!isUrlMapMode || !!initialBounds)

	const handleViewChange = (bounds: [number, number, number, number], zoom: number) => {
		// El mapa sigue montado (precargado/oculto) en mobile cuando viewMode=0 (lista) — sus
		// resize/moveend no deben disparar una nueva búsqueda ni pisar la búsqueda de lista actual.
		// Tampoco cuando el bottom-sheet tapa (casi) todo el mapa (hoy solo 'full'; si 'half' se
		// reactiva, esto sigue siendo correcto porque compara la proporción tapada, no el nombre
		// del snap) — ahí no queda viewport visible sobre el que buscar, y buscar igual vacía el listado.
		const isMobileNow = typeof window !== 'undefined' && window.innerWidth <= 767;
		const mapMostlyCovered = isMobileNow && typeof window !== 'undefined' && mobileBottomInsetPx >= window.innerHeight * 0.85;
		if (viewMode === 0 || mapMostlyCovered) return;
		const viewboardStr = bounds.map(n => n.toFixed(6)).join(',');
		const zoomStr = zoom.toFixed(2);
		// Guardar sincrónicamente para que handleViewModeChange pueda incluirlo
		// aunque router.query aún no haya sido actualizado por el replace
		latestMapQueryRef.current = { viewboard: viewboardStr, zoom: zoomStr, b: '1' };
		router.replace(
			{ pathname: router.pathname, query: { ...router.query, viewboard: viewboardStr, zoom: zoomStr, b: '1' } },
			undefined,
			{ shallow: true }
		);
		const viewboardFiltro: IFiltros = {
			id: 'viewboard',
			type: 'radio',
			name: 'viewboard',
			values: [{ id: 0, label: 'bbox', value: bounds }],
		};
		// Mantener actualizado para que usePropertySearch lo inyecte en cambios de filtros
		viewboardFiltroRef.current = [viewboardFiltro];
		// Habilitar auto-execute de usePropertySearch a partir de ahora (tenemos viewboard)
		mapViewboardReadyRef.current = true;
		const filtrosSinViewboard = filterState.filtros.filter(f => f.id !== 'viewboard');
		// Cancelar tanto el search pendiente de usePropertySearch como el anterior de mapa
		searchPendingRef.current?.abort();
		pendingMapRequestRef.current?.abort();
		pendingMapRequestRef.current = dispatch(fetchByCriteria({ ...filterState, filtros: [...filtrosSinViewboard, viewboardFiltro], page: 1 }));
	};

	const handleBreakPolygon = () => {
		const prioritized = ['pasaje', 'calle', 'avenida', 'barrio', 'comuna', 'region'];
		for (const id of prioritized) {
			if (filterState.filtros.find(f => f.id === id)) {
				removeFilterRef.current?.removeFilterReplace(id);
				break;
			}
		}
	};

	const handleViewModeChange = (mode: ViewMode) => {
		// PRUEBA: vista "lista+mapa" deshabilitada en mobile — se fuerza a mapa completo.
		// Para revertir, quitar este ajuste y usar `mode` directamente.
		const isMobileNow = typeof window !== 'undefined' && window.innerWidth <= 767;
		const effectiveMode: ViewMode = isMobileNow && mode === 2 ? 1 : mode;
		setViewMode(effectiveMode);
		const label = effectiveMode === 1 ? 'm' : effectiveMode === 2 ? 'ml' : 'l';
		const restQuery = { ...router.query };
		delete restQuery.view;
		// Fusionar con el último viewboard/zoom conocido para no perderlo por race condition
		// cuando router.query aún no refleja el replace que acaba de hacer handleViewChange
		const mergedQuery = { ...latestMapQueryRef.current, ...restQuery };
		const newQuery = effectiveMode === 0 ? mergedQuery : { ...mergedQuery, view: label };
		router.push({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
	};
	const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

	const { computedRegionSlug, footerData } = props;
	const { banners, filtros, idsRequeridos, seo } = estructuraDinamica ?? {};
	const { title, description } = seo.meta ?? {}

	const { banners: dynamicBanners, seo: dynamicSeo } = useBannerUpdater(
		filterState.filtros,
		estructuraDinamica.urlSlug,
		banners,
		null,
		seo
	);

	const currentBanners = dynamicBanners || banners;
	const currentSeo = dynamicSeo || seo;
	const pathCanonical = pathListaSeo as string[] ?? []
	const urlCanonical = "/" + pathCanonical.join("/")
	const url = props.urlBase ? props.urlBase + urlCanonical : 'https://www.toctoc.com' + urlCanonical;
	const ssrFilterState = (props.initialReduxState?.FilterState ?? {}) as IFilterContext;
	const NEXT_PUBLIC_ID_VWO = props.NEXT_PUBLIC_ID_VWO;
	let ssrFilterStateSerialized = serializeFilterValue(ssrFilterState);
	ssrFilterStateSerialized += ssrFilterState.order + "";

	const bajadaTitulo = generateBajadaTitulo(propertiesCountState, filtrosBaseDinamica);

	// isMobile: empieza en false (igual en SSR y primer render cliente → sin hydration error)
	// useEffect lo actualiza después de montar — solo afecta props menores, no la estructura del layout
	const [isMobile, setIsMobile] = useState(false)
	useEffect(() => {
		const check = () => setIsMobile(window.innerWidth <= 767)
		check()
		window.addEventListener('resize', check)
		return () => window.removeEventListener('resize', check)
	}, [])

	const [mapReady, setMapReady] = useState(false)

	// Derivar una clave única del filtro geográfico activo (pasaje > calle > avenida > barrio > comuna > region).
	// Prioridad: más específico primero. Solo desde filterState.filtros — no se mezcla filtrosBaseDinamica
	// para evitar re-fetches espurios cuando la estructura base cambia por shallow navigation.
	const polygonKey = (() => {
		const pasaje = filterState.filtros.find(f => f.id === 'pasaje')?.values?.[0]?.label
		if (pasaje) {
			return `pasaje:${pasaje}`
		}
		const calle = filterState.filtros.find(f => f.id === 'calle')?.values?.[0]?.label
		if (calle) {
			return `calle:${calle}`
		}
		const avenida = filterState.filtros.find(f => f.id === 'avenida')?.values?.[0]?.label
		if (avenida) {
			return `avenida:${avenida}`
		}
		const barrio = filterState.filtros.find(f => f.id === 'barrio')?.values?.[0]?.value?.[0]
		if (barrio) {
			return `barrio:${barrio}`
		}
		const commune = filterState.filtros.find(f => f.id === 'comuna')?.values?.[0]?.label
		if (commune != null) {
			return `commune:${commune}`
		}
		const region = filterState.filtros.find(f => f.id === 'region')?.values?.[0]?.label
		if (region != null) {
			return `region:${region}`
		}
		return null
	})()

	useEffect(() => {
		if (!polygonKey) {
			setPolygon(null)
			return
		}
		const baseUrl = process.env.NEXT_PUBLIC_HOME_BASE_API
		const colonIdx = polygonKey.indexOf(':')
		const type = polygonKey.slice(0, colonIdx)
		const value = polygonKey.slice(colonIdx + 1)
		let param = `region=${value}`
		if (type === 'avenida') param = `avenida=${encodeURIComponent(value)}`
		else if (type === 'calle') param = `calle=${encodeURIComponent(value)}`
		else if (type === 'pasaje') param = `pasaje=${encodeURIComponent(value)}`
		else if (type === 'barrio') param = `barrio=${encodeURIComponent(value)}`
		else if (type === 'commune') param = `commune=${value}`

		const controller = new AbortController()
		fetch(`${baseUrl}/polygons?${param}`, { signal: controller.signal })
			.then(r => {
				return r.ok ? r.json() : null
			})
			.then(data => {
				if (data) setPolygon(data.polygon ?? null)
			})
			.catch(e => {
				if (e?.name !== 'AbortError') setPolygon(null)
			})

		return () => controller.abort()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [polygonKey])

	// En mobile sin ?view en la URL, auto-switch a mapa en cuanto se confirma isMobile.
	// Ref para que sea one-shot: no re-dispara si el usuario vuelve a lista (que elimina ?view).
	const mobileAutoSwitchedRef = useRef(false)
	useEffect(() => {
		if (!router.isReady || mobileAutoSwitchedRef.current) return;
		if (isMobile && !router.query.view) {
			mobileAutoSwitchedRef.current = true
			handleViewModeChange(1)
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobile, router.isReady])

	// Sincronizar URL con el snap del bottom sheet en mobile
	// collapsed → ?view=m | half → ?view=ml | full → sin view (lista pura)
	useEffect(() => {
		if (!isMobile || viewMode === 0) return;
		const snapToView: Record<BottomSheetSnap, string> = {
			collapsed: 'm',
			// PRUEBA: vista "lista+mapa" deshabilitada en mobile — half ya no mapea a 'ml'.
			// Para revertir: half: 'ml',
			half: 'm',
			// full cubre el mapa pero sigue en modo mapa — back button es la salida al layout lista
			full: 'm',
		};
		const targetView = snapToView[bottomSheetSnap];
		const currentView = (router.query.view as string | undefined) ?? '';
		if (targetView === currentView) return;
		router.replace(
			{ pathname: router.pathname, query: { ...router.query, view: targetView } },
			undefined,
			{ shallow: true }
		);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bottomSheetSnap, isMobile])

	const { triggerSearchRef: triggerSearch, pendingRef: searchPendingRef } = usePropertySearch(
		filterState,
		filterStateSerialized,
		{
			actionDelay: 800,
			firstSearchDelay: 0,
			initialFilterStateSerialized: ssrFilterStateSerialized,
			// En modo mapa sin viewboard en URL, el mapa dispara el primer fetch via handleViewChange.
			// Una vez que el mapa llama handleViewChange, mapViewboardReadyRef pasa a true y los
			// cambios de filtros vuelven a auto-ejecutar con el viewboard inyectado.
			autoExecute: mapViewboardReadyRef.current,
			extraFiltrosRef: viewMode !== 0 ? viewboardFiltroRef : undefined,
		}
	);

	const propsSwiper = {
		navigation: false,
		spaceBetween: 16,
		slidesPerView: 1,
		loop: false,
		autoplay: true,
		delay: 7000,
		breakpoints: { 600: { slidesPerView: 2 }, 980: { slidesPerView: 3 } },
		pagination: { clickable: true }
	};

	const propsSwiperMobile = {
		navigation: false,
		spaceBetween: 0,
		slidesPerView: 1,
		loop: false,
		autoplay: true,
		delay: 7000,
		pagination: { clickable: true }
	};

	const comunaLabel = filterState.filtros.find(f => f.id === "comuna")?.values[0]?.label;
	const regionLabel = filterState.filtros.find(f => f.id === "region")?.values[0]?.label;
	const fallbackComunaLabel = filterState.filtros.length === 0 ? filtrosBaseDinamica.find(x => x.id === 'comuna')?.values[0]?.label : null;
	const fallbackRegionLabel = filterState.filtros.length === 0 ? filtrosBaseDinamica.find(x => x.id === 'region')?.values[0]?.label : null;
	const displayRegion = regionLabel || fallbackRegionLabel || '';
	const displayComuna = comunaLabel || fallbackComunaLabel || '';

	const guestSignUpModalHandler = (showModal: boolean) => dispatch(setShowGuestSignUpModal(showModal));
	const quoteStatusModalHandler = (showModal: boolean) => dispatch(setShowQuoteStatusModal(showModal));
	const contactStatusModalHandler = (showModal: boolean) => dispatch(setShowContactStatusModal(showModal));
	const selectedPropertyDataHandler = (selectedPropertyData: IPropertyCard) => dispatch(setSelectedPropertyData(selectedPropertyData));
	const generateLead = (leadData: LeadRequest) => dispatch(sendLead(leadData));
	const saveTempLogInData = (leadData: TempUserData) => dispatch(setTempLogInData(leadData));

	// Favorito: requiere sesión LOGEADA real. isGuest (invitado, ej. tras cotizar) no cuenta —
	// un invitado sin cuenta no tiene dónde persistir el favorito, por eso igual se manda al SSO.
	// El modal GuestSignUpModal no se usa acá: ese modal es solo para contactar/cotizar.
	const favoriteClickHandler = (property: IPropertyCard) => {
		// El identificador para favoritos es siempre hashId — sin fallback a idProperty. Si la
		// propiedad no tiene hashId, la acción falla (el backend la rechazaría igual).
		if (!property.hashId) return;

		if (isLogin && userData?.email) {
			const currentlyFavorite = favoritesState[property.idProperty] ?? property.esFavorita ?? false;
			const newValue = !currentlyFavorite;
			dispatch(toggleFavoriteOptimistic(property.idProperty));
			dispatch(sendFavorite({ idProperty: property.idProperty, hashId: property.hashId, email: userData.email, favorite: newValue }));
			return;
		}

		// URL de retorno completa (con todos los query params actuales: filtros, viewboard, página, etc.)
		// + el parámetro que identifica la propiedad a favoritear automáticamente al volver logeado
		// (hashId, el mismo identificador que se manda al backend).
		// Se arma desde router.asPath (no window.location.href) porque es el estado canónico de
		// Next.js: con navegación shallow (viewboard/zoom/b se actualizan así) queda siempre en
		// sync, mientras que window.location.href puede quedar un instante atrás.
		const currentPage = new URL(router.asPath, window.location.origin);
		currentPage.searchParams.set(FAVORITE_RETURN_PARAM, property.hashId);
		const returnUrl = Buffer.from(currentPage.toString()).toString('base64');
		window.location.href = `${process.env.NEXT_PUBLIC_URL_SSO}?url=${returnUrl}`;
	}

	// Al volver logeado desde el SSO, aplica automáticamente el favorito indicado por FAVORITE_RETURN_PARAM
	// (hashId) en la URL, y luego lo quita de la URL para no repetir la acción en un refresh/back.
	const appliedFavoriteReturnRef = useRef(false);
	useEffect(() => {
		if (!router.isReady || appliedFavoriteReturnRef.current) return;
		const rawHashId = router.query[FAVORITE_RETURN_PARAM];
		if (!rawHashId) return;
		if (!isLogin || !userData?.email) return;

		appliedFavoriteReturnRef.current = true;
		const hashId = Array.isArray(rawHashId) ? rawHashId[0] : rawHashId;
		// Busca la propiedad ya cargada solo para actualizar el ícono optimista local
		// (favoritesState se indexa por idProperty) — el favorito se manda igual aunque no se
		// encuentre, porque para el backend alcanza con el hashId.
		const matchedProperty = [...propertiesList, ...mapProperties].find(p => p.hashId === hashId);
		if (matchedProperty) dispatch(setFavorite({ idProperty: matchedProperty.idProperty, value: true }));
		dispatch(sendFavorite({ idProperty: matchedProperty?.idProperty, hashId, email: userData.email, favorite: true }));

		const newQuery = { ...router.query };
		delete newQuery[FAVORITE_RETURN_PARAM];
		router.replace({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.isReady, router.query, isLogin, userData?.email, dispatch, router]);

	// Guardar búsqueda: persiste la URL completa (con todos los filtros/query strings actuales).
	// Igual que el favorito, requiere sesión logeada real; si no hay sesión, se manda al SSO.
	//
	// El estado "guardado" vive en el store (savedSearchUrl), no en un timer local: una vez
	// guardada la búsqueda actual, el botón queda en su cara "Guardado" (deshabilitado) y no
	// vuelve a "Guardar" salvo que la ruta cambie a una distinta de la guardada.
	const savedSearchUrl = useAppSelector(selectSavedSearchUrl);

	// URL completa (con query strings) a persistir, siempre sin el parámetro de retorno del SSO.
	// Solo puede calcularse en cliente (usa window.location.origin) — en SSR devuelve null.
	const buildSearchUrlToSave = () => {
		if (typeof window === 'undefined') return null;
		const currentPage = new URL(router.asPath, window.location.origin);
		currentPage.searchParams.delete(SAVE_SEARCH_RETURN_PARAM);
		return currentPage.toString();
	}

	const currentSearchUrl = buildSearchUrlToSave();
	const isCurrentSearchSaved = !!currentSearchUrl && savedSearchUrl === currentSearchUrl;

	const saveSearchClickHandler = () => {
		if (isCurrentSearchSaved || !currentSearchUrl) return;

		if (isLogin && userData?.email) {
			dispatch(setSavedUrl(currentSearchUrl));
			dispatch(sendUserSearch({ email: userData.email, url: currentSearchUrl }));
			return;
		}

		const currentPage = new URL(router.asPath, window.location.origin);
		currentPage.searchParams.set(SAVE_SEARCH_RETURN_PARAM, '1');
		const returnUrl = Buffer.from(currentPage.toString()).toString('base64');
		window.location.href = `${process.env.NEXT_PUBLIC_URL_SSO}?url=${returnUrl}`;
	}

	// Al volver logeado desde el SSO, guarda automáticamente la búsqueda indicada por
	// SAVE_SEARCH_RETURN_PARAM, y luego lo quita de la URL para no repetir la acción en un refresh/back.
	const appliedSaveSearchReturnRef = useRef(false);
	useEffect(() => {
		if (!router.isReady || appliedSaveSearchReturnRef.current) return;
		const rawFlag = router.query[SAVE_SEARCH_RETURN_PARAM];
		if (!rawFlag) return;
		if (!isLogin || !userData?.email) return;

		const url = buildSearchUrlToSave();
		if (!url) return;

		appliedSaveSearchReturnRef.current = true;
		dispatch(setSavedUrl(url));
		dispatch(sendUserSearch({ email: userData.email, url }));

		const newQuery = { ...router.query };
		delete newQuery[SAVE_SEARCH_RETURN_PARAM];
		router.replace({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.isReady, router.query, isLogin, userData?.email, dispatch, router]);

	const cardProps = {
		cardWide: false as const,
		medio: currentBanners.medio,
		listaSeoUrl: pathCanonical.join('/'),
		sessionInfo,
		guestSignUpModalHandler,
		selectedPropertyDataHandler,
		generateLead,
		favoriteClickHandler,
		quoteAndContactEnabled: quoteAndContactEnabled as boolean,
	}

	const isLoading = propertiesStatusState === STATUS.pending || propertiesStatusState === STATUS.init;
	const isEmpty = propertiesCountState === 0;

	const headContent = (
		<Head>
			<title>{title}</title>
			<meta name="description" content={description} />
			<meta property="og:title" content={title} />
			<meta property="og:description" content={description} />
			<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: getSchema(propertiesList) }} />
			<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: getBreadcrumbSchema(props.urlBase, tof, tpf, region, comuna, chainSegment) }} />
			<link rel="canonical" href={url}></link>
		</Head>
	)

	// Alto visible del bottom sheet mobile (overlay fixed) según su snap — se resta del viewboard
	// del mapa (ver MapboxMap bottomInsetPx) para no buscar/centrar en el área que tapa la lista.
	// Valores calcados de SNAP_TRANSLATE en MobileBottomSheet.tsx.
	const mobileBottomInsetPx = (() => {
		if (!isMobile || viewMode === 0 || typeof window === 'undefined') return 0
		if (bottomSheetSnap === 'collapsed') return 80
		if (bottomSheetSnap === 'half') return window.innerHeight * 0.5
		return window.innerHeight - 64 // full
	})()

	const modals = (
		<>
			{showGuestSignUpModal && <GuestSignUpModal isMobile={isMobile} cloudfrontUrl={urlTocTocImg} leadStatus={leadStatus} showModal={showGuestSignUpModal} guestSignUpModalHandler={guestSignUpModalHandler} selectedPropertyData={selectedPropertyData} generateLead={generateLead} saveTempLogInData={saveTempLogInData} listaSeoUrl={pathCanonical.join('/')} />}
			{showQuoteStatusModal && <QuoteStatusModal isMobile={isMobile} showModal={showQuoteStatusModal} quoteStatusModalHandler={quoteStatusModalHandler} selectedPropertyData={selectedPropertyData} leadStatusInfo={leadStatusInfo} />}
			{showContactStatusModal && <ContactStatusModal isMobile={isMobile} showModal={showContactStatusModal} contactStatusModalHandler={contactStatusModalHandler} leadStatusInfo={leadStatusInfo} userData={userData} />}
		</>
	)

	return (
		<>
			{headContent}
			{!mapReady && (
				<div className={styles.mobileLoadingOverlay}>
					<svg className={styles.mobileLoadingLogo} viewBox="0 0 169.4 44.1" xmlns="http://www.w3.org/2000/svg" aria-label="TocToc">
						<path fill="#37FFDB" className={styles.mobileLoadingArrow} style={{ animationDelay: '0s' }}   d="M15.5,22.5c0.5-1.6-0.4-3.2-2-3.7l-9.7-2.9c-1.6-0.5-3.2,0.4-3.7,2c-0.5,1.6,0.4,3.2,2,3.7l0,0l9.7,2.9C13.4,25,15.1,24.1,15.5,22.5L15.5,22.5z" />
						<path fill="#37FFDB" className={styles.mobileLoadingArrow} style={{ animationDelay: '0.2s' }} d="M16.5,30c-0.8-1.4-2.6-2-4-1.2l-5.6,3.1c-1.5,0.8-2,2.5-1.3,4c0.8,1.5,2.5,2,4,1.3c0,0,0.1,0,0.1-0.1l5.6-3.1C16.8,33.3,17.3,31.5,16.5,30z" />
						<path fill="#37FFDB" className={styles.mobileLoadingArrow} style={{ animationDelay: '0.4s' }} d="M15.1,15c1,1.3,2.9,1.4,4.2,0.4c1.3-1,1.4-2.9,0.4-4.2L11.3,1.1c-1-1.3-2.9-1.5-4.2-0.5S5.6,3.5,6.6,4.8l0.1,0.1L15.1,15z" />
						<path fill="#37FFDB" className={styles.mobileLoadingArrow} style={{ animationDelay: '0.6s' }} d="M21,35.7c-1.5-0.5-3.2,0.3-3.8,1.8L16.4,40c-0.6,1.5,0.1,3.2,1.6,3.9c1.5,0.6,3.2-0.1,3.9-1.6c0-0.1,0.1-0.2,0.1-0.3l0.9-2.5C23.4,37.9,22.6,36.2,21,35.7z" />
						<path fill="#3200C1" d="M37,18.6v16.5c0,0.8-0.6,1.4-1.4,1.4h-2.8c-0.8,0-1.4-0.6-1.4-1.4V18.6c0-0.5-0.4-0.9-0.9-0.9h-4.9c-0.8,0-1.5-0.7-1.5-1.5l0,0v-1.6c0-0.8,0.7-1.5,1.5-1.5l0,0h17.3c0.8,0,1.5,0.7,1.5,1.5l0,0v1.6c0,0.8-0.7,1.5-1.5,1.5l0,0H38C37.4,17.7,37,18.1,37,18.6z M48.9,33.4c-4.8-4.7-4.9-12.3-0.3-17.1c0.1-0.1,0.2-0.2,0.3-0.3c2.4-2.3,5.5-3.5,9.2-3.5c3.6,0,6.7,1.2,9.2,3.5c4.8,4.6,4.9,12.3,0.3,17.1c-0.1,0.1-0.2,0.2-0.3,0.3c-2.4,2.3-5.5,3.5-9.2,3.5S51.3,35.8,48.9,33.4z M58.1,31.8c1.9,0,3.7-0.7,5.1-2c2.7-2.8,2.7-7.3,0-10.1c-2.8-2.7-7.3-2.7-10.1,0c-2.7,2.8-2.7,7.3,0,10.1C54.4,31.1,56.2,31.9,58.1,31.8L58.1,31.8z M77.1,33.4c-4.8-4.7-4.9-12.3-0.2-17.1c0.1-0.1,0.1-0.1,0.2-0.2c2.4-2.3,5.5-3.5,9.1-3.5c1.9,0,3.8,0.4,5.5,1.2c1,0.5,1.9,1.1,2.8,1.8c0.8,0.7,0.9,1.9,0.2,2.6c-0.1,0.1-0.1,0.1-0.2,0.2l-1.2,1c-0.6,0.5-1.5,0.5-2.1,0c-1.4-1.2-3.1-1.8-5-1.8c-3.9,0-7.1,3.2-7.1,7.1s3.2,7.1,7.1,7.1c1.9,0,3.7-0.6,5.1-1.9c0.5-0.5,1.2-0.5,1.8,0l1.8,1.5c0.6,0.5,0.7,1.4,0.2,2L95,33.5C89.9,38.1,82.2,38.1,77.1,33.4L77.1,33.4z M110.8,18.6v16.5c0,0.8-0.6,1.4-1.4,1.4l0,0h-2.8c-0.8,0-1.4-0.6-1.4-1.4l0,0V18.6c0-0.5-0.4-0.9-0.9-0.9l0,0h-4.9c-0.8,0-1.5-0.7-1.5-1.5l0,0v-1.6c0-0.8,0.7-1.5,1.5-1.5l0,0h17.4c0.8,0,1.5,0.7,1.5,1.5l0,0v1.6c0,0.8-0.7,1.5-1.5,1.5l0,0h-5C111.2,17.7,110.8,18.1,110.8,18.6L110.8,18.6z M122.7,33.4c-4.8-4.7-4.9-12.3-0.3-17.1c0.1-0.1,0.2-0.2,0.3-0.3c2.4-2.3,5.5-3.5,9.2-3.5c3.6,0,6.7,1.2,9.2,3.5c4.8,4.6,4.9,12.3,0.3,17.1c-0.1,0.1-0.2,0.2-0.3,0.3c-2.4,2.3-5.5,3.5-9.2,3.5S125.2,35.8,122.7,33.4z M131.9,31.8c1.9,0,3.7-0.7,5.1-2c2.7-2.8,2.7-7.3,0-10.1c-2.8-2.7-7.3-2.7-10.1,0c-2.7,2.8-2.7,7.3,0,10.1C128.2,31.1,130,31.9,131.9,31.8z M151,33.4c-4.8-4.7-4.9-12.3-0.2-17.1c0.1-0.1,0.1-0.1,0.2-0.2c2.4-2.3,5.4-3.5,9.1-3.5c1.9,0,3.8,0.4,5.5,1.2c1,0.5,1.9,1.1,2.8,1.8c0.8,0.7,0.9,1.9,0.2,2.6c-0.1,0.1-0.1,0.1-0.2,0.2l-1.2,1c-0.6,0.5-1.5,0.5-2.1,0c-1.4-1.2-3.1-1.8-5-1.8c-3.9,0-7.1,3.2-7.1,7.1s3.2,7.1,7.1,7.1c1.9,0,3.7-0.6,5.1-1.9c0.5-0.5,1.2-0.5,1.8,0l1.8,1.5c0.6,0.5,0.7,1.4,0.2,2l-0.1,0.1C163.7,38.1,156,38.1,151,33.4L151,33.4z" />
					</svg>
				</div>
			)}

			{/* Breadcrumbs / título / banners — ocultos en mobile cuando hay mapa */}
			<ContainerStyled className={viewMode !== 0 ? styles.mobileHideOnMap : undefined}>
				<GoogleTagManager idGTM={props.idGTM} />
				<VWO idVWO={NEXT_PUBLIC_ID_VWO} />
				<Breadcrumbs urlBase={props.urlBase} tipoOperacionFamilia={tof} tipoPropiedadFamilia={tpf} region={displayRegion} comuna={displayComuna} isMobile={isMobile} comunaPath={comuna} regionPath={region} chain={chainSegment} data-origen={url} />
				<div className='cTT'>
					<h1 className='ttBPLIndex'>{currentSeo.title}</h1>
					<p className='bajadaBPLIndex'>{bajadaTitulo}</p>
				</div>
				{/* Banners ocultos en mobile — descomentar cuando se definan los formatos mobile */}
				<div className={styles.desktopOnly}>
					<Carousel
						{...propsSwiper}
						slideItems={<>{
							currentBanners?.superior?.map((x: IBanner, index: number) => (
								<a key={x.order} target='_blank' href={x.url} id="banner-superior">
									<img src={x.imagenes.srcDefault} width={'100%'} style={{ border: "solid 1px #f4f4f4", borderRadius: "10px" }} alt={x.alt} data-apartado={`banner_lista_seo_superior_${index + 1}`} data-nombre-campana="nombre_campaña_superior" />
								</a>
							))
						}</>}
					/>
				</div>
			</ContainerStyled>

			{/* Barra de búsqueda + chips — oculta en mobile cuando hay mapa */}
			<section className={`${styles.searchSection}${viewMode !== 0 ? ` ${styles.mobileHideOnMap}` : ''}`}>
				<div className={styles.pageContainer}>
					<SearchFilterBar
						onOpenFilters={() => setFiltersDrawerOpen(true)}
						viewMode={viewMode}
						onViewModeChange={handleViewModeChange}
						tipoOperacionFamilia={tof}
						tipoPropiedadFamilia={tpf}
						isMobile={isMobile}
						onSave={saveSearchClickHandler}
						saved={isCurrentSearchSaved}
					/>
					<div className={styles.chipsWrapper}>
						<FilterChips
							urlSlug={estructuraDinamica.urlSlug}
							onRemoveFilter={(id) => removeFilterRef.current?.removeFilter(id)}
							isMobile={isMobile}
						/>
					</div>
				</div>
			</section>

			{/*
			  Sección de resultados — NUNCA oculta con display:none.
			  El mapa vive aquí con position:fixed en mobile vía CSS,
			  por lo que no importa que el padre esté en el flujo normal.
			*/}
			<section className={styles.resultsSection}>
				<div className={styles.pageContainer}>

					{/* OrdenarPor: visible en desktop; en mobile solo en lista (viewMode=0) */}
					{(viewMode === 0 || viewMode === 1) && (
						<div className={viewMode !== 0 ? styles.mobileHideOnMap : undefined}>
							<OrdenarPorV2
								totalPropiedades={propertiesCountState}
								tipoOperacionFamilia={tof}
								tipoPropiedadFamilia={tpf}
								isLoading={isLoading}
							/>
						</div>
					)}

					{/* Lista pura */}
					{viewMode === 0 && (
						isLoading ? (
							<PropertyCardSkeletonGrid columns={4} count={Math.min(propertiesList.length || 8, 20)} />
						) : isEmpty ? (
							<NotFound />
						) : (
							<>
								<ListadoPropiedades columns={4} {...cardProps} />
								<div className={styles.paginationWrapper}>
									<PaginationIndex
										data-apartado={`${tof} ${tpf}`}
										totalPropiedades={propertiesCountState}
									/>
								</div>
							</>
						)
					)}

					{/* Mapa: visible en viewMode 1/2; en mobile también se monta en viewMode=0 para precargar */}
					{(viewMode !== 0 || isMobile) && (
						<div className={viewMode === 2 ? styles.splitLayout : undefined}>

							{/* Columna de lista en split — oculta en mobile */}
							{viewMode === 2 && (
								<div className={`${styles.cardsSide} ${styles.mobileHideOnMap}`}>
									<OrdenarPorV2
										totalPropiedades={propertiesCountState}
										tipoOperacionFamilia={tof}
										tipoPropiedadFamilia={tpf}
										isLoading={isLoading}
									/>
									{isLoading ? (
										<PropertyCardSkeletonGrid columns={2} count={Math.min(propertiesList.length || 6, 20)} />
									) : isEmpty ? (
										<NotFound />
									) : (
										<>
											<ListadoPropiedades
												columns={2}
												{...cardProps}
												onPropertyHover={setHoveredPropertyId}
												highlightedPropertyId={hoveredPropertyId}
											/>
											<div className={styles.paginationWrapper}>
												<PaginationIndex
													data-apartado={`${tof} ${tpf}`}
													totalPropiedades={propertiesCountState}
												/>
											</div>
										</>
									)}
								</div>
							)}

							{/*
							  Contenedor del mapa:
							  - Desktop: layout normal (mapFullPage / mapSide)
							  - Mobile: position:fixed vía CSS (.mobileMapContainer) — no importa
							    que los ancestros estén en flujo normal; fixed escapa del flujo.
							    Solo display:none en un ancestro lo ocultaría — y ninguno lo tiene.
							*/}
							<div
								className={[
									viewMode === 1 ? styles.mapFullPage : styles.mapSide,
									styles.mobileMapContainer,
									viewMode === 0 && isMobile ? styles.mobileMapPreload : '',
								].join(' ')}
								style={{
									// Posición y visibilidad del logo/atribución de Mapbox según snap del sheet.
									// En 'full' el mapa queda cubierto → ocultar controles; resto → encima del sheet.
									// Cuando hay card visible, se empuja la atribución sobre ella para no taparla
									// (card min-height ≈ 90px; gap 8px → +98px sobre la posición base de la card).
									'--mapbox-ctrl-bottom': (() => {
										if (bottomSheetSnap === 'full') return '88px'
										const cardUp = !!selectedMarkerProperty
										if (bottomSheetSnap === 'half') {
											return cardUp ? 'calc(50vh + 106px)' : 'calc(50vh + 8px)'
										}
										// collapsed
										return cardUp ? 'calc(186px + env(safe-area-inset-bottom))' : '88px'
									})(),
									'--mapbox-ctrl-opacity': bottomSheetSnap === 'full' ? '0' : '1',
								} as React.CSSProperties}
							>
								<MapboxMap
									properties={mapProperties}
									total={propertiesCountState}
									height="100%"
									initialBounds={initialBounds}
									initialZoom={initialZoom}
									onViewChange={handleViewChange}
									polygon={polygon}
									onBreakPolygon={handleBreakPolygon}
									geoFilterChangeKey={geoFilterChangeKey}
									resizeTrigger={viewMode}
									bottomInsetPx={mobileBottomInsetPx}
									hoveredPropertyId={hoveredPropertyId}
									onMarkerClick={(property) => {
										setSelectedMarkerProperty(property)
										setHoveredPropertyId(property.idProperty)
									}}
									onMarkerHover={(id) => setHoveredPropertyId(id)}
									isMobile={isMobile}
									onMapReady={() => setMapReady(true)}
									onVisiblePropertiesChange={(ids) => { visiblePropertyIdsRef.current = ids }}
								/>

								{/*
								  Overlays mobile dentro del contenedor del mapa.
								  Al compartir el mismo contexto de apilamiento (z-index: 100 del padre),
								  los controles de Mapbox (logo + atribución) pueden tener z-index > 300
								  y aparecer encima del bottom sheet sin necesitar un portal externo.
								*/}
								{viewMode !== 0 && (
									<MobileMapHeader
										title={currentSeo.title}
										count={propertiesCountState}
										onBack={() => handleViewModeChange(0)}
										onOpenFilters={() => setFiltersDrawerOpen(true)}
										tipoOperacionFamilia={tof}
										tipoPropiedadFamilia={tpf}
									/>
								)}

								{viewMode !== 0 && (
									<MobileBottomSheet
										snap={bottomSheetSnap}
										onSnapChange={setBottomSheetSnap}
										totalPropiedades={propertiesCountState}
									>
										<OrdenarPorV2
											totalPropiedades={propertiesCountState}
											tipoOperacionFamilia={tof}
											tipoPropiedadFamilia={tpf}
											isLoading={isLoading}
										/>
										<FilterChips
											urlSlug={estructuraDinamica.urlSlug}
											onRemoveFilter={(id) => removeFilterRef.current?.removeFilter(id)}
											isMobile={isMobile}
										/>
										{isLoading ? (
											<GlobalLoader />
										) : isEmpty ? (
											<NotFound />
										) : (
											<>
												<h2 className={`ttBPLIndex ${styles.mobileBannerTitle}`}>{currentSeo.title}</h2>
												{(currentBanners?.superior?.length ?? 0) > 0 && (
													<div className={styles.mobileBannerWrapper}>
														<Carousel
															{...propsSwiperMobile}
															slideItems={<>{
																currentBanners?.superior?.map((x: IBanner, index: number) => (
																	<a key={x.order} target='_blank' href={x.url} id="banner-superior">
																		<img src={x.imagenes.srcDefault} width={'100%'} style={{ border: 'solid 1px #f4f4f4', borderRadius: '10px' }} alt={x.alt} data-apartado={`banner_lista_seo_superior_${index + 1}`} data-nombre-campana="nombre_campaña_superior" />
																	</a>
																))
															}</>}
														/>
													</div>
												)}
												<ListadoPropiedades columns={2} {...cardProps} onVerMas={(p) => setDetailProperty(p)} />
												<div className={styles.paginationWrapper}>
													<PaginationIndex
														data-apartado={`${tof} ${tpf}`}
														totalPropiedades={propertiesCountState}
													/>
												</div>
												<FooterIndex footerData={footerData} />
											</>
										)}
									</MobileBottomSheet>
								)}

								{selectedMarkerProperty && (
									<MiniPropertyCard
										property={selectedMarkerProperty}
										onFavoriteClick={favoriteClickHandler}
										onClose={() => {
											setSelectedMarkerProperty(null)
											setHoveredPropertyId(null)
										}}
										onOpenDetail={() => setDetailProperty(selectedMarkerProperty)}
										snap={bottomSheetSnap}
										onSwipeLeft={() => {
											const visible = mapProperties.filter(
												p => p.latitud != null && p.longitud != null && visiblePropertyIdsRef.current.includes(p.idProperty)
											)
											const idx = visible.findIndex(p => p.idProperty === selectedMarkerProperty.idProperty)
											const next = visible[idx + 1]
											if (next) { setSelectedMarkerProperty(next); setHoveredPropertyId(next.idProperty); return true }
											return false
										}}
										onSwipeRight={() => {
											const visible = mapProperties.filter(
												p => p.latitud != null && p.longitud != null && visiblePropertyIdsRef.current.includes(p.idProperty)
											)
											const idx = visible.findIndex(p => p.idProperty === selectedMarkerProperty.idProperty)
											const prev = visible[idx - 1]
											if (prev) { setSelectedMarkerProperty(prev); setHoveredPropertyId(prev.idProperty); return true }
											return false
										}}
									/>
								)}

								{detailProperty && isMobile && (
									<MobilePropertyDetailModal
										property={detailProperty}
										onClose={() => setDetailProperty(null)}
										quoteAndContactEnabled={quoteAndContactEnabled as boolean}
										onContact={() => {
											const p = detailProperty
											selectedPropertyDataHandler(p)
											const isListaSeoItau = pathCanonical.join('/').indexOf('itau') !== -1
											if (userData) {
												generateLead({
													propertyId: p.idProperty,
													hashId: p.hashId,
													originId: isListaSeoItau
														? eOriginId.listaSeoItau
														: p.idEstadoPropiedad === ePropertyCondition.new
															? eOriginId.listaSeoQuote
															: eOriginId.listaSeoContact,
													email: userData.email,
													utmSource: pathCanonical.join('/'),
													isNewProperty: p.idEstadoPropiedad === ePropertyCondition.new,
												})
											}
											guestSignUpModalHandler(true)
										}}
									/>
								)}
							</div>
						</div>
					)}

					{viewMode === 0 && <TextoSeo seoText={seo.seoText} />}
				</div>
			</section>

			{/* Botón flotante "Mapa": visible en mobile siempre que la lista se vea a pantalla completa,
			    ya sea en viewMode=0 (lista pura) o en viewMode!=0 con el bottom sheet en snap 'full'
			    (el sheet cubre visualmente el mapa aunque view siga siendo 'm' en la URL). */}
			{isMobile && (viewMode === 0 || bottomSheetSnap === 'full') && (
				<FloatingMapButton
					onClick={() => {
						if (viewMode === 0) handleViewModeChange(1)
						else setBottomSheetSnap('collapsed')
					}}
				/>
			)}

			<FiltersDrawer
				open={filtersDrawerOpen}
				onClose={() => setFiltersDrawerOpen(false)}
				triggerSearch={triggerSearch}
				filtros={filtros}
				idsRequeridos={idsRequeridos}
				isMobile={isMobile}
				filtrosBase={filtrosBaseDinamica}
				urlSlug={estructuraDinamica.urlSlug}
				pathListaSeo={pathCanonical}
				onFiltersReady={(control) => { removeFilterRef.current = control }}
				forceShallowNavigation={true}
			/>

			{modals}

			{computedRegionSlug && <Prefooter regionContent={computedRegionSlug} />}

			<FooterIndex footerData={footerData} />
		</>
	)
}
